var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { serverConnection } from "../server/ServerConnectionService";
import { DeferredPromise, Delegate1, MulticastDelegate1 } from "flavr-common";
import { usersService } from "../UsersService";
import { busService } from "../../bus";
import { PeerFileChannelClosedMessage, PeerFileErrorMessage, PeerFileMessage, PeerFileChannelOpenedMessage } from "../../../messages/file/FileMessages";
import { PeerStateChangedMessage } from "../../../messages/session/PeerMessages";
import { fetchWebRTCStunTurnServers } from "./IceServers";
import { AwaitQueue } from "./AwaitQueue";
import { LoadingSpinnerMessage } from "../../../messages/loading/LoadingSpinnerMessage";
// https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Perfect_negotiation
const WAITING_FOR_ANSWER_TIMEOUT = 2 * 60 * 1000;
const DEBUG_PRINT_LOGS = false;
// https://github.com/jitsi/lib-jitsi-meet/blob/e6779627b712db1be3489aa75695a03f9aa4b0be/modules/RTC/RTCUtils.js
// https://github.com/bigbluebutton/bigbluebutton/blob/ee1a10d217f1e9b1171bc45cf2048f57d4b19f66/bigbluebutton-html5/public/compatibility/adapter.js
// https://github.com/versatica/mediasoup-client/blob/7979926723282dd0462c568332ddaafcd3b7ce28/src/handlers/sdp/unifiedPlanUtils.ts
// https://github.com/mdn/samples-server/blob/master/s/webrtc-from-chat/chatserver.js
const supportsSetCodecPreferences = window.RTCRtpTransceiver && "setCodecPreferences" in window.RTCRtpTransceiver.prototype;
const RTCPeerConnection = window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection || window.msRTCPeerConnection;
if (RTCPeerConnection === undefined) {
    throw new Error("This browser does not seem to support WebRTC.");
}
// ToDo: If the packet size is larger than 65535 Bytes, this can lead to errors during serialization.
// Chromium ignores the EOR flag.
// When Firefox and Chrome talk to each other, the max packet size is only 16384 bytes.
// https://lgrahl.de/articles/demystifying-webrtc-dc-size-limit.html
const MAX_DATA_MESSAGE_SIZE = 16384; // 65535;
// Audio bandwith unlimited
// const AUDIO_BANDWIDTH = 0;
const MAXIMUM_FILE_CHUNK_SIZE = 65535;
const END_OF_FILE_MESSAGE = "EOF";
const DISCONNECT_TIMEOUT = 5 * 60 * 1000;
var FileState;
(function (FileState) {
    FileState[FileState["INIT"] = 0] = "INIT";
    FileState[FileState["WAIT_FOR_METADATA"] = 1] = "WAIT_FOR_METADATA";
    FileState[FileState["LOADING"] = 2] = "LOADING";
    FileState[FileState["FINISHED"] = 3] = "FINISHED";
})(FileState || (FileState = {}));
const busConnection = busService.connection("WebRTC");
// handle of peer-to-peer connections for data and audio stream
export class WebRTC {
    constructor(id, clientId, remoteClientId, initiator = false) {
        this.id = id;
        this.clientId = clientId;
        this.remoteClientId = remoteClientId;
        this.initiator = initiator;
        this.onMessageCallbacks = [];
        this.onBufferCallbacks = [];
        this.onChannelsOpenedCallbacks = [];
        this.destroyMeDelegate = new Delegate1();
        this.connectedDelegate = new Delegate1();
        this.maxDataChannelBufferedAmount = MAX_DATA_MESSAGE_SIZE;
        this.isUnreliableBufferedAmountHigh = false;
        this.isReliableBufferedAmountHigh = false;
        this.isBusBufferedAmountHigh = false;
        this.dataChannelFiles = new Map();
        this.pendingIceCandidates = [];
        this.needsCreateOffer = false;
        this._maximumMessageSize = 65535;
        this.dataChannelRegistry = new Map();
        this.wasMeDestroyed = false;
        this.wasDisconnectedTimeout = false;
        this.iceCompleted = false;
        this.updateLog = [];
        this.isUsingTurn = false;
        this.offerVersion = 0;
        this.answerVersion = 0;
        this.configuration = {
            iceServers: [],
            iceTransportPolicy: "all",
            bundlePolicy: "max-bundle",
            rtcpMuxPolicy: "require"
        };
        this.awaitQueue = new AwaitQueue("webRTC", {
            ClosedErrorClass: Error
        });
        this.onAnswerDelegate = new MulticastDelegate1();
        this.onicecandidateerror = (event) => {
            this.logUpdate("icecandidateerror", event.error);
        };
        this.onicegatheringstatechange = () => {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.logUpdate("iceGatheringStateChange", this.peerConnection.iceGatheringState);
            switch (this.peerConnection.iceGatheringState) {
                case "failed":
                    // The ICE candidate has checked all candidates pairs against one another and has failed to find
                    // compatible matches for all components of the connection. It is, however, possible that the ICE agent
                    // did find compatible connections for some components.
                    break;
                case "disconnected":
                    // Checks to ensure that components are still connected failed for at least one component of the
                    // RTCPeerConnection. This is a less stringent test than failed and may trigger intermittently and
                    // resolve just as spontaneously on less reliable networks, or during temporary disconnections.
                    // When the problem resolves, the connection may return to the connected state.
                    break;
                case "closed": // The ICE agent for this RTCPeerConnection has shut down and is no longer handling requests.
                    break;
            }
        };
        this.onconnectionstatechange = () => __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.logUpdate("connectionStateChange", this.peerConnection.connectionState);
            if (this.peerConnection.connectionState === "failed") {
                // Chrome triggers 'failed' for connectionState event, only
                yield this.handleIceNegotiationFailed();
            }
            if (!this.peerConnection)
                return;
            switch (this.peerConnection.connectionState) {
                case "failed":
                case "closed":
                case "disconnected":
                    busConnection.send(new PeerStateChangedMessage(this.remoteClientId, "connection", this.peerConnection.connectionState));
                    break;
                default:
                    break;
            }
        });
        this.onsignalingstatechange = () => __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.logUpdate("signalingStateChange", this.peerConnection.signalingState);
            if (this.peerConnection.signalingState === "closed") {
                busConnection.send(new PeerStateChangedMessage(this.remoteClientId, "signaling", this.peerConnection.signalingState));
            }
            if (this.peerConnection.signalingState !== "stable")
                return;
            if (this.needsCreateOffer) {
                this.needsCreateOffer = false;
                this.logUpdate("create offer while signal state change", this.peerConnection.signalingState);
                yield this.createOffer();
            }
        });
        this.onNegotiationNeeded = () => __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                console.error("[WebRTC] PeerConnection not set");
                return;
            }
            this.logUpdate("onRenegotiationNeeded");
            if (this.peerConnection.signalingState !== "stable") {
                this.needsCreateOffer = true;
                console.warn("[WebRTC] onNegotiationNeeded: signaling isn't stable");
                return;
            }
            this.needsCreateOffer = false;
            yield this.createOffer();
        });
        this.onICECandidate = (event) => {
            // candidate === "" end of candidates
            if (!event.candidate) {
                return;
            }
            serverConnection.send({
                channel: "rtc:ice",
                payload: {
                    id: this.id,
                    candidate: event.candidate,
                    target: this.remoteClientId
                }
            });
            this.logUpdate("onIceCandidate", event.candidate);
        };
        this.onICEConnectionStateChange = () => __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                console.error("[WebRTC] onICEConnectionStateChange: PeerConnection not set");
                return;
            }
            switch (this.peerConnection.iceConnectionState) {
                case "completed":
                    this.logUpdate("ice connection completed");
                    break;
                case "connected":
                    this.logUpdate("ice connection connected");
                    this.clearDisconnectTimeout();
                    this.wasDisconnectedTimeout = false;
                    if ((this.iceNegotiationTimeout === undefined)) {
                        clearTimeout(this.iceNegotiationTimeout);
                        this.iceNegotiationTimeout = undefined;
                    }
                    if (this.restartICETimeout !== undefined) {
                        clearTimeout(this.restartICETimeout);
                        this.restartICETimeout = undefined;
                    }
                    if (this.wasMeDestroyed) {
                        console.error("[WebRTC] Avatar was destroyed, reconnect");
                    }
                    else {
                        this.connectedDelegate.invoke(this);
                    }
                    this.checkIsUsingTurn();
                    this.iceCompleted = true;
                    break;
                case "closed":
                    console.warn("ice connection closed");
                    // this.setDisconnectTimeout();
                    break;
                case "failed":
                    console.warn("ice connection failed");
                    this.setDisconnectTimeout();
                    yield this.handleIceNegotiationFailed();
                    break;
                case "disconnected":
                    /*
                    In Plan B, when unable to connect or connection is lost,
                    iceConnectionState may either report “disconnected” or “failed”.
                    But in Unified Plan, we generally report “disconnected” instead of “failed”.
                    This is something your application has to be prepared for,
                    and it may be worth treating being “disconnected” or having been “disconnected” for a few seconds as an indication that we have “failed”.
                    */
                    this.logUpdate("ice connection disconnected");
                    this.setDisconnectTimeout();
                    break;
                case "checking":
                    this.clearDisconnectTimeout();
                    if (this.restartICETimeout !== undefined) {
                        clearTimeout(this.restartICETimeout);
                        this.restartICETimeout = undefined;
                    }
                    break;
                default:
                    this.logUpdate("oniceconnectionstatechange", this.peerConnection.iceConnectionState);
                    break;
            }
            switch (this.peerConnection.iceConnectionState) {
                case "failed":
                case "closed":
                case "disconnected":
                    busConnection.send(new PeerStateChangedMessage(this.remoteClientId, "ice", this.peerConnection.iceConnectionState));
                    break;
                default:
                    break;
            }
        });
        this.onDisconnectTimeout = () => {
            console.error(`Client ${usersService.getDisplayName(this.remoteClientId)} no longer responds`);
            this.wasDisconnectedTimeout = true;
            void this.recreate();
        };
        this.registerDataChannels();
    }
    registerOnMessage(cb) {
        this.onMessageCallbacks.push(cb);
    }
    registerOnBuffer(cb) {
        this.onBufferCallbacks.push(cb);
    }
    registerOnChannelsOpened(cb) {
        this.onChannelsOpenedCallbacks.push(cb);
    }
    recreate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearDisconnectTimeout();
            this.close();
            yield this.create(true);
        });
    }
    create() {
        return __awaiter(this, arguments, void 0, function* (forceICEServers = false) {
            var _a;
            this.configuration.iceServers = yield fetchWebRTCStunTurnServers(forceICEServers);
            this.peerConnection = new RTCPeerConnection(this.configuration);
            this.peerConnection.onicecandidateerror = this.onicecandidateerror;
            this.peerConnection.onicegatheringstatechange = this.onicegatheringstatechange;
            this.peerConnection.onconnectionstatechange = this.onconnectionstatechange;
            this.peerConnection.onicecandidate = this.onICECandidate;
            this.peerConnection.oniceconnectionstatechange = this.onICEConnectionStateChange;
            this.peerConnection.onsignalingstatechange = this.onsignalingstatechange;
            this.peerConnection.onnegotiationneeded = this.onNegotiationNeeded;
            this.maxDataChannelBufferedAmount = Math.max(this.maxMessageSize * 8, 1048576); // 8 max messages size or at least 1 MiB
            if (supportsSetCodecPreferences) {
                const capabilities = RTCRtpSender.getCapabilities("audio");
                let capabilitiesMsg = "[multiplayer] Create WebRTC connection with the following capabilities:\n";
                if (capabilities !== null) {
                    for (const codec of capabilities.codecs) {
                        if (["video/red", "video/ulpfec", "video/rtx"].includes(codec.mimeType)) {
                            continue;
                        }
                        const value = (codec.mimeType + " " + ((_a = codec.sdpFmtpLine) !== null && _a !== void 0 ? _a : "")).trim();
                        capabilitiesMsg += `--- codec: ${value}\n`;
                    }
                }
                else {
                    capabilitiesMsg += `--- no audio capabilities supported!`;
                }
                if (DEBUG_PRINT_LOGS)
                    console.log("[multiplayer]", capabilitiesMsg);
            }
            // Don't wait for open data channels
            // init data channels should tigger onNegotiationNeeded
            void this.initDataChannels();
        });
    }
    getPeerConnection() {
        return this.peerConnection;
    }
    getWasDisconnectedTimeout() {
        return this.wasDisconnectedTimeout;
    }
    /**
     *
     * @returns
     * new
     * At least one of the connection's ICE transports (RTCIceTransport or RTCDtlsTransport objects) is in the new state, and none of them are in one of the following states: connecting, checking, failed, disconnected, or all of the connection's transports are in the closed state.
     *
     * connecting
     * One or more of the ICE transports are currently in the process of establishing a connection; that is, their iceConnectionState is either checking or connected, and no transports are in the failed state.
     *
     * connected
     * Every ICE transport used by the connection is either in use (state connected or completed) or is closed (state closed); in addition, at least one transport is either connected or completed.
     *
     * disconnected
     * At least one of the ICE transports for the connection is in the disconnected state and none of the other transports are in the state failed, connecting, or checking.
     *
     * failed
     * One or more of the ICE transports on the connection is in the failed state.
     *
     * closed
     * The RTCPeerConnection is closed.
     */
    getConnectionState() {
        if (!this.peerConnection) {
            return "closed";
        }
        return this.peerConnection.connectionState;
    }
    close() {
        if (!this.peerConnection) {
            return;
        }
        this.peerConnection.ontrack = null;
        this.peerConnection.onicecandidate = null;
        this.peerConnection.oniceconnectionstatechange = null;
        this.peerConnection.onsignalingstatechange = null;
        this.peerConnection.onicegatheringstatechange = null;
        this.peerConnection.onnegotiationneeded = null;
        this.peerConnection.onicecandidateerror = null;
        this.peerConnection.onconnectionstatechange = null;
        for (const transceiver of this.peerConnection.getTransceivers()) {
            transceiver.stop();
        }
        this.awaitQueue.close();
        try {
            if (this.dataChannelReliable) {
                this.dataChannelReliable.close();
                this.dataChannelReliable = undefined;
            }
        }
        catch (error) {
            console.error("[multiplayer]", error);
        }
        try {
            if (this.dataChannelUnreliable) {
                this.dataChannelUnreliable.close();
                this.dataChannelUnreliable = undefined;
            }
        }
        catch (error) {
            console.error("[multiplayer]", error);
        }
        try {
            if (this.dataChannelBusMessage) {
                this.dataChannelBusMessage.close();
                this.dataChannelBusMessage = undefined;
            }
        }
        catch (error) {
            console.error("[multiplayer]", error);
        }
        for (const data of this.dataChannelFiles.values()) {
            try {
                data.channel.close();
            }
            catch (error) {
                console.error("[multiplayer]", error);
            }
        }
        this.dataChannelFiles.clear();
        // wait for closing finished?
        try {
            this.peerConnection.close();
            this.peerConnection = undefined;
        }
        catch (error) {
            console.error("[multiplayer] Error in close", error);
        }
    }
    onServerDisconnect() {
        this.clearDisconnectTimeout();
        this.close();
    }
    isDataChannelUnreliableReady() {
        if (!this.dataChannelUnreliable)
            return false;
        if (this.dataChannelUnreliable.readyState !== "open")
            return false;
        if (this.dataChannelUnreliable.bufferedAmount > this.maxDataChannelBufferedAmount) {
            this.isUnreliableBufferedAmountHigh = true;
            return false;
        }
        if (this.isUnreliableBufferedAmountHigh) {
            if (this.dataChannelUnreliable.bufferedAmount > this.maxMessageSize) {
                return false;
            }
            this.isUnreliableBufferedAmountHigh = false;
        }
        return true;
    }
    isDataChannelReliableReady() {
        if (!this.dataChannelReliable)
            return false;
        if (this.dataChannelReliable.readyState !== "open")
            return false;
        if (this.dataChannelReliable.bufferedAmount > this.maxDataChannelBufferedAmount) {
            this.isReliableBufferedAmountHigh = true;
            return false;
        }
        if (this.isReliableBufferedAmountHigh) {
            if (this.dataChannelReliable.bufferedAmount > this.maxMessageSize) {
                return false;
            }
            this.isReliableBufferedAmountHigh = false;
        }
        return true;
    }
    isDataChannelBusMessageReady() {
        if (!this.dataChannelBusMessage)
            return false;
        if (this.dataChannelBusMessage.readyState !== "open")
            return false;
        if (this.dataChannelBusMessage.bufferedAmount > this.maxDataChannelBufferedAmount) {
            this.isBusBufferedAmountHigh = true;
            return false;
        }
        if (this.isBusBufferedAmountHigh) {
            if (this.dataChannelBusMessage.bufferedAmount > this.maxMessageSize) {
                return false;
            }
            this.isBusBufferedAmountHigh = false;
        }
        return true;
    }
    get maxMessageSize() {
        if (!this.peerConnection || this.peerConnection.sctp === undefined || this.peerConnection.sctp === null) {
            return Math.min(this._maximumMessageSize, MAX_DATA_MESSAGE_SIZE);
        }
        return Math.min(this.peerConnection.sctp.maxMessageSize, MAX_DATA_MESSAGE_SIZE);
    }
    onDestroy() { }
    createOffer(_offerOption) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this._createOffer(_offerOption);
            }), "createOffer")
                .catch((error) => {
                console.warn(error);
            });
        });
    }
    _createOffer(_offerOption) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.checkSignalingStateIs("createOffer", "stable");
            const offerOption = _offerOption !== null && _offerOption !== void 0 ? _offerOption : {};
            try {
                this.logUpdate("createOffer", { options: offerOption });
                const offer = yield this.peerConnection.createOffer(offerOption);
                this.logUpdate("createOfferOnSuccess", offer);
                if (this.peerConnection.signalingState !== "stable") {
                    console.warn("The peer connection isn't stable yet. postponing...", this.remoteClientId);
                    return;
                }
                const desc = new RTCSessionDescription(offer);
                yield this.setLocalDescription(desc);
                if (!offer.sdp) {
                    return;
                }
                this.checkSignalingStateIs("createOffer", "have-local-offer");
                const sdp = offer.sdp;
                serverConnection.send({
                    channel: "rtc:offer",
                    payload: {
                        id: this.id,
                        sdp,
                        target: this.remoteClientId
                    }
                });
                try {
                    yield this.waitForAnswer();
                }
                catch (error) {
                    console.error("[WebRTC]", error);
                    if (this.peerConnection && this.peerConnection.signalingState === "have-local-offer") {
                        yield this.peerConnection.setLocalDescription({ type: "rollback" });
                    }
                    throw error;
                }
            }
            catch (error) {
                console.error(`Failed to create session description: `, error);
                throw error;
            }
        });
    }
    waitForAnswer() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve, reject) => {
                let timeoutHandle;
                const func = (error) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    if (timeoutHandle !== undefined) {
                        self.clearTimeout(timeoutHandle);
                        timeoutHandle = undefined;
                    }
                    this.onAnswerDelegate.remove(func);
                    resolve();
                };
                // wait for answer
                timeoutHandle = self.setTimeout(() => {
                    this.onAnswerDelegate.remove(func);
                    timeoutHandle = undefined;
                    reject(new Error(`waiting for answer timeout. ${this.remoteClientId}`));
                }, WAITING_FOR_ANSWER_TIMEOUT);
                this.onAnswerDelegate.add(func);
            });
        });
    }
    handleOffer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this._handleOffer(sdp);
            }), "handleOffer")
                .catch((error) => {
                console.error("[WebRTC]", error);
            });
        });
    }
    _handleOffer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            this.offerVersion++;
            if (!this.peerConnection /* || this.peerConnection.signalingState === "have-local-offer" */) {
                return;
            }
            this.checkSignalingStateIs("handleOffer", "stable");
            const desc = new RTCSessionDescription({ type: "offer", sdp });
            if (this.peerConnection.signalingState !== "stable") {
                console.warn(`The signaling state isn't stable "${this.peerConnection.signalingState}", so triggering rollback`);
            }
            const match = sdp.match(/a=max-message-size:\s*(\d+)/);
            if (match !== null && match.length >= 2) {
                this._maximumMessageSize = parseInt(match[1]);
            }
            try {
                // https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Signaling_and_video_calling#signaling_transaction_flow
                yield this.setRemoteDescription(desc);
                this.checkSignalingStateIs("handleOffer", "have-remote-offer");
                this.logUpdate("createAnswer");
                const answerDesc = yield this.peerConnection.createAnswer();
                this.logUpdate("createAnswerOnSuccess", answerDesc);
                if (!answerDesc.sdp) {
                    console.error("Error by createAnswer: sdp is undefined");
                    return;
                }
                yield this.setLocalDescription(answerDesc);
                this.checkSignalingStateIs("handleOffer", "stable");
                sdp = answerDesc.sdp;
                serverConnection.send({
                    channel: "rtc:answer",
                    payload: {
                        id: this.id,
                        sdp,
                        target: this.remoteClientId
                    }
                });
                yield this.processPendingIceCandidates();
            }
            catch (error) {
                console.error("[WebRTC]", error);
                // inform other peer
                serverConnection.send({
                    channel: "rtc:answer",
                    payload: {
                        id: this.id,
                        sdp: "",
                        target: this.remoteClientId
                    }
                });
                throw error;
            }
        });
    }
    handleAnswer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!sdp) {
                    this.onAnswerDelegate.invoke(new Error("Peer can't answer"));
                    return;
                }
                this.answerVersion++;
                if (!this.peerConnection) {
                    console.warn(`[WebRTC] Can't send answer because there is no peer connection: ${this.answerVersion}`);
                    this.onAnswerDelegate.invoke(new Error("PeerConnection not defined"));
                    return;
                }
                this.checkSignalingStateIs("handleAnswer", "have-local-offer");
                const answer = { type: "answer", sdp };
                yield this.setRemoteDescription(answer);
                if (!this.peerConnection) {
                    throw new Error("PeerConnection not set");
                }
                this.checkSignalingStateIs("handleAnswer", "stable");
                yield this.processPendingIceCandidates();
                this.onAnswerDelegate.invoke(undefined);
            }
            catch (error) {
                console.error("[WebRTC] Error in handleAnswer", error);
                this.onAnswerDelegate.invoke(error);
            }
        });
    }
    handleIce(candidate) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this._handleIce(candidate);
            }), "handleAnswer")
                .catch((error) => {
                console.error("[WebRTC]", error);
            });
        });
    }
    _handleIce(candidate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!candidate) {
                return;
            }
            if (!this.peerConnection || !this.peerConnection.remoteDescription || this.peerConnection.remoteDescription.sdp === "") {
                this.pendingIceCandidates.push(candidate);
            }
            else {
                this.logUpdate("addIceCandidate", candidate);
                yield this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch((error) => {
                    console.error("[WebRTC]", error.toString());
                });
            }
        });
    }
    restartIce() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                if (!this.peerConnection)
                    return;
                if ("restartIce" in this.peerConnection) {
                    this.peerConnection.restartIce();
                    return;
                }
                yield this._createOffer({
                    iceRestart: true
                });
            }), "transport.restartIce()")
                .catch((error) => {
                console.error("[WebRTC]", error);
            });
            // ToDo: if closed recreate connection
        });
    }
    sendReliable(msg) {
        if (!this.dataChannelReliable || this.dataChannelReliable.readyState !== "open")
            return false;
        const maxMessageSize = this.maxMessageSize;
        try {
            if (msg.length + 1 < maxMessageSize) {
                this.dataChannelReliable.send(`${msg}@`);
            }
            else {
                for (let offset = 0; offset < msg.length + 1; offset += maxMessageSize) {
                    if (offset + maxMessageSize < msg.length + 1) {
                        this.dataChannelReliable.send(msg.substring(offset, maxMessageSize));
                    }
                    else {
                        this.dataChannelReliable.send(`${msg.substring(offset)}@`);
                    }
                }
            }
            return true;
        }
        catch (error) {
            console.error("[WebRTC] sendReliable", error);
        }
        return false;
    }
    sendReliableBuffer(buffer) {
        if (!this.dataChannelReliable || this.dataChannelReliable.readyState !== "open")
            return false;
        if (buffer.byteLength > this.maxMessageSize) {
            console.error(`sendReliableBuffer: buffer length "${buffer.byteLength}" too large. Max ${this.maxMessageSize}`);
            return false;
        }
        try {
            this.dataChannelReliable.send(buffer);
            return true;
        }
        catch (error) {
            console.error("[WebRTC] sendReliableBuffer", error);
        }
        return false;
    }
    sendUnreliable(msg) {
        if (!this.dataChannelUnreliable || this.dataChannelUnreliable.readyState !== "open")
            return false;
        if (msg.length > this.maxMessageSize) {
            console.error(`[WebRTC] sendReliableBuffer \n--- msg length "${msg.length}" too large \n--- max length is ${this.maxMessageSize}`);
            return false;
        }
        try {
            this.dataChannelUnreliable.send(msg);
            return true;
        }
        catch (error) {
            console.error("[WebRTC] Error while sendUnreliable", error);
        }
        return false;
    }
    sendUnreliableBuffer(buffer) {
        if (!this.dataChannelUnreliable || this.dataChannelUnreliable.readyState !== "open")
            return false;
        if (buffer.byteLength > this.maxMessageSize) {
            console.error(`sendReliableBuffer: buffer length "${buffer.byteLength}" too large. Max ${this.maxMessageSize}`);
            return false;
        }
        try {
            this.dataChannelUnreliable.send(buffer);
            return true;
        }
        catch (error) {
            console.error("[WebRTC] sendUnreliableBuffer", error);
        }
        return false;
    }
    sendBusMessage(buffer) {
        if (!this.dataChannelBusMessage || this.dataChannelBusMessage.readyState !== "open")
            return false;
        if (buffer.byteLength > this.maxMessageSize) {
            console.error(`sendReliableBuffer: buffer length "${buffer.byteLength}" too large. Max ${this.maxMessageSize}`);
            return false;
        }
        try {
            this.dataChannelBusMessage.send(buffer);
            return true;
        }
        catch (error) {
            console.error("[WebRTC] sendBusMessage", error);
            // ignore
            return false;
        }
    }
    sendFile(file, metaData) {
        return __awaiter(this, void 0, void 0, function* () {
            const fileChannel = this.createDataChannel("file");
            fileChannel.binaryType = "arraybuffer";
            metaData.fileType = "file";
            yield new Promise((resolve, reject) => {
                fileChannel.onopen = () => __awaiter(this, void 0, void 0, function* () {
                    const chunkSize = Math.min(this.maxMessageSize, MAXIMUM_FILE_CHUNK_SIZE);
                    const lowWaterMark = chunkSize;
                    const highWaterMark = Math.max(chunkSize * 8, 1048576); // 8 chunks or at least 1 MiB
                    fileChannel.bufferedAmountLowThreshold = lowWaterMark;
                    busConnection.send(new PeerFileChannelOpenedMessage(this.clientId, metaData.fileId, metaData.filename));
                    try {
                        // send file meta data first
                        fileChannel.send(JSON.stringify(metaData));
                        const arrayBuffer = yield file.arrayBuffer();
                        for (let i = 0; i < arrayBuffer.byteLength; i += chunkSize) {
                            if (fileChannel.bufferedAmount > highWaterMark) {
                                // Pause sending if we reach the high water mark
                                yield this.waitForBufferedAmountLow(fileChannel);
                            }
                            fileChannel.send(arrayBuffer.slice(i, i + chunkSize));
                        }
                        fileChannel.send(END_OF_FILE_MESSAGE);
                    }
                    catch (error) {
                        reject(error);
                    }
                });
                fileChannel.onerror = (event) => {
                    busConnection.send(new PeerFileErrorMessage(this.clientId, metaData.fileId, JSON.stringify(event.error)));
                };
                fileChannel.onclose = () => {
                    busConnection.send(new PeerFileChannelClosedMessage(this.clientId, metaData.fileId));
                    resolve();
                };
            });
        });
    }
    sendFileString(data, metaData) {
        return __awaiter(this, void 0, void 0, function* () {
            const channel = this.createDataChannel("file");
            channel.binaryType = "arraybuffer";
            metaData.fileType = "string";
            yield new Promise((resolve, reject) => {
                channel.onopen = () => __awaiter(this, void 0, void 0, function* () {
                    const chunkSize = Math.min(this.maxMessageSize, MAXIMUM_FILE_CHUNK_SIZE);
                    const lowWaterMark = chunkSize;
                    const highWaterMark = Math.max(chunkSize * 8, 1048576); // 8 chunks or at least 1 MiB
                    channel.bufferedAmountLowThreshold = lowWaterMark;
                    busConnection.send(new PeerFileChannelOpenedMessage(this.clientId, metaData.fileId, metaData.filename));
                    try {
                        // send file meta data first
                        channel.send(JSON.stringify(metaData));
                        for (let i = 0; i < data.length; i += chunkSize) {
                            if (channel.bufferedAmount > highWaterMark) {
                                // Pause sending if we reach the high water mark
                                yield this.waitForBufferedAmountLow(channel);
                            }
                            const str = data.substring(i, i + chunkSize);
                            channel.send(str);
                        }
                        channel.send(END_OF_FILE_MESSAGE);
                    }
                    catch (_a) {
                        reject();
                    }
                });
                channel.onerror = (event) => {
                    busConnection.send(new PeerFileErrorMessage(this.clientId, metaData.fileId, JSON.stringify(event.error)));
                };
                channel.onclose = () => {
                    busConnection.send(new PeerFileChannelClosedMessage(this.clientId, metaData.fileId));
                    resolve();
                };
            });
        });
    }
    getStats(peerStatistics) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                return yield Promise.resolve({ forEach: () => { } });
            }
            const stats = yield this.peerConnection.getStats(null);
            stats.forEach((report) => {
                let bitrateSent = 0;
                let bitrateReceived = 0;
                if (this.lastStatsResults && report.timestamp && (report.bytesSent || report.bytesReceived)) {
                    const lastResult = this.lastStatsResults.get(report.id);
                    if (!lastResult)
                        return;
                    if (lastResult.timestamp < report.timestamp) {
                        if (report.bytesSent) {
                            bitrateSent = (8 * (report.bytesSent - lastResult.bytesSent)) / (report.timestamp - lastResult.timestamp);
                        }
                        if (report.bytesReceived) {
                            bitrateReceived = (8 * (report.bytesReceived - lastResult.bytesReceived)) / (report.timestamp - lastResult.timestamp);
                        }
                    }
                }
                if (report.type === "data-channel") {
                    this.setStats(peerStatistics, report.label, {
                        timestamp: report.timestamp,
                        bytesSent: report.bytesSent,
                        bytesReceived: report.bytesReceived,
                        packetsSent: report.messagesSent,
                        packetsReceived: report.messagesReceived,
                        bitrateSent: bitrateSent,
                        bitrateReceived: bitrateReceived
                    });
                    // Chromium
                    /* bytesReceived: 165
                    bytesSent: 189
                    datachannelid: 1
                    id: "RTCDataChannel_3"
                    label: "reliable"
                    messagesReceived: 2
                    messagesSent: 2
                    protocol: ""
                    state: "open"
                    timestamp: 1574848275660.229
                    type: "data-channel" */
                }
                else if (report.type === "candidate-pair") {
                    this.setStats(peerStatistics, "candidate", {
                        timestamp: report.timestamp,
                        bytesSent: report.bytesSent,
                        bytesReceived: report.bytesReceived,
                        packetsSent: report.requestsSent,
                        packetsReceived: report.requestsReceived,
                        bitrateSent: bitrateSent,
                        bitrateReceived: bitrateReceived
                    });
                    // Chromium
                    /* bytesReceived: 18115
                    bytesSent: 8933
                    consentRequestsSent: 5
                    currentRoundTripTime: 0.003
                    id: "RTCIceCandidatePair_OlV2c22m_1iJv+GYa"
                    localCandidateId: "RTCIceCandidate_OlV2c22m"
                    nominated: true
                    priority: 9079290933588934000
                    remoteCandidateId: "RTCIceCandidate_1iJv+GYa"
                    requestsReceived: 6
                    requestsSent: 1
                    responsesReceived: 6
                    responsesSent: 6
                    state: "succeeded"
                    timestamp: 1574848275660.229
                    totalRoundTripTime: 0.022
                    transportId: "RTCTransport_0_1"
                    type: "candidate-pair"
                    writable: true */
                }
                else if ((report.type === "outbound-rtp" || report.type === "inbound-rtp") && report.mediaType === "audio") {
                    let stats = peerStatistics.get("audio");
                    if (!stats) {
                        stats = {
                            timestamp: 0,
                            bytesSent: 0,
                            bytesReceived: 0,
                            packetsSent: 0,
                            packetsReceived: 0,
                            bitrateSent: 0,
                            bitrateReceived: 0
                        };
                        peerStatistics.set("audio", stats);
                    }
                    stats.timestamp = report.timestamp;
                    if (report.bytesSent) {
                        stats.bytesSent = report.bytesSent;
                        stats.bitrateSent = bitrateSent;
                    }
                    if (report.bytesReceived) {
                        stats.bytesReceived = report.bytesReceived;
                        stats.bitrateReceived = bitrateReceived;
                    }
                    if (report.packetsSent)
                        stats.packetsSent = report.packetsSent;
                    if (report.packetsReceived)
                        stats.packetsReceived = report.packetsReceived;
                    // Chromium
                    /* bytesSent: 120678
                    codecId: "RTCCodec_0_Outbound_111"
                    id: "RTCOutboundRTPAudioStream_2406925535"
                    isRemote: false
                    kind: "audio"
                    mediaSourceId: "RTCAudioSource_1"
                    mediaType: "audio"
                    packetsSent: 1297
                    retransmittedBytesSent: 0
                    retransmittedPacketsSent: 0
                    ssrc: 2406925535
                    timestamp: 1574848678006.466
                    trackId: "RTCMediaStreamTrack_sender_1"
                    transportId: "RTCTransport_0_1"
                    type: "outbound-rtp" */
                }
                else if ((report.type === "outbound-rtp" || report.type === "inbound-rtp") && report.mediaType === "video") {
                    let stats = peerStatistics.get("video");
                    if (!stats) {
                        stats = {
                            timestamp: 0,
                            bytesSent: 0,
                            bytesReceived: 0,
                            packetsSent: 0,
                            packetsReceived: 0,
                            bitrateSent: 0,
                            bitrateReceived: 0
                        };
                        peerStatistics.set("video", stats);
                    }
                    stats.timestamp = report.timestamp;
                    if (report.bytesSent) {
                        stats.bytesSent = report.bytesSent;
                        stats.bitrateSent = bitrateSent;
                    }
                    if (report.bytesReceived) {
                        stats.bytesReceived = report.bytesReceived;
                        stats.bitrateReceived = bitrateReceived;
                    }
                    if (report.packetsSent)
                        stats.packetsSent = report.packetsSent;
                    if (report.packetsReceived)
                        stats.packetsReceived = report.packetsReceived;
                }
                else if (report.type === "transport") {
                    // Chromium
                    /* bytesReceived: 1101626
                    bytesSent: 1127915
                    dtlsState: "connected"
                    id: "RTCTransport_0_1"
                    localCertificateId: "RTCCertificate_0C:5A:5B:4B:83:C7:92:63:3E:76:92:94:2C:AC:49:F1:CC:C7:65:F6:35:55:4E:32:B7:F3:32:9C:5E:6B:C1:2E"
                    remoteCertificateId: "RTCCertificate_54:4A:86:09:DF:AF:39:66:98:0A:06:A1:8C:4B:C3:A7:9C:05:12:4B:51:AF:70:01:87:7F:FF:88:02:10:FA:BB"
                    selectedCandidatePairChanges: 1
                    selectedCandidatePairId: "RTCIceCandidatePair_OlV2c22m_1iJv+GYa"
                    timestamp: 1574848678006.466
                    type: "transport" */
                }
            });
            this.lastStatsResults = stats;
            return stats;
        });
    }
    waitForBufferedAmountLow(channel_1) {
        return __awaiter(this, arguments, void 0, function* (channel, timeout = 30000) {
            yield new Promise((resolve, reject) => {
                const func = (_ev) => {
                    if (timeoutTime !== undefined) {
                        clearTimeout(timeoutTime);
                    }
                    try {
                        resolve();
                    }
                    catch (error) {
                        reject(error);
                    }
                };
                const timeoutFunc = () => {
                    if (channel.bufferedAmount > channel.bufferedAmountLowThreshold) {
                        timeoutTime = setTimeout(timeoutFunc, timeout);
                        return;
                    }
                    // bufferedamountlow event isn't fired
                    channel.removeEventListener("bufferedamountlow", func);
                    try {
                        resolve();
                    }
                    catch (error) {
                        reject(error);
                    }
                };
                let timeoutTime = setTimeout(timeoutFunc, timeout);
                channel.addEventListener("bufferedamountlow", func, { once: true });
            });
        });
    }
    handleMessage(data, label) {
        if (typeof data === "string") {
            const d = JSON.parse(data);
            if (this.onMessageCallbacks.length === 0) {
                throw new Error("no callback register");
            }
            for (const cb of this.onMessageCallbacks) {
                cb(d, label);
            }
        }
        else if (data instanceof ArrayBuffer) {
            if (this.onBufferCallbacks.length === 0) {
                throw new Error("no callback register");
            }
            for (const cb of this.onBufferCallbacks) {
                cb(data, label);
            }
        }
        else if (data instanceof Blob) {
            console.warn("[WebRTC] received Blob in message, however Blobs are currently unsupported!");
        }
    }
    registerDataChannels() {
        this.dataChannelRegistry.set("reliable", {
            onSetChannel: (channel) => {
                this.dataChannelReliable = channel;
            },
            params: {
                ordered: true
            }
        });
        this.dataChannelRegistry.set("unreliable", {
            onSetChannel: (channel) => {
                this.dataChannelUnreliable = channel;
            },
            params: {
                ordered: false,
                maxRetransmits: 0
            }
        });
        this.dataChannelRegistry.set("bus", {
            onSetChannel: (channel) => {
                this.dataChannelBusMessage = channel;
            },
            params: {
                ordered: true
            }
        });
    }
    initInitiatorDataChannels(promises) {
        if (!this.peerConnection) {
            throw new Error("PeerConnection not set");
        }
        for (const [label, data] of this.dataChannelRegistry) {
            const channel = this.createDataChannel(label, data.params);
            channel.binaryType = "arraybuffer";
            data.onSetChannel(channel);
            promises.push(this.resolveOnOpenChannel(channel));
            if (label === "reliable") {
                channel.onmessage = ({ data }) => {
                    if (typeof data === "string") {
                        this.onMessage(data, label);
                    }
                    else {
                        this.handleMessage(data, label);
                    }
                };
            }
            else {
                channel.onmessage = ({ data }) => {
                    this.handleMessage(data, label);
                };
            }
        }
        this.dataChannelRegistry.clear();
        this.peerConnection.ondatachannel = ({ channel }) => {
            if (channel.label === "file") {
                this.onFileDataChannel(channel);
            }
        };
    }
    initOtherDataChannel(promises) {
        if (!this.peerConnection) {
            throw new Error("PeerConnection not set");
        }
        const channelPromises = [];
        for (let i = 0, len = this.dataChannelRegistry.size; i < len; ++i) {
            const deferred = new DeferredPromise();
            channelPromises.push(deferred);
            promises.push(deferred.promise);
        }
        this.peerConnection.ondatachannel = ({ channel }) => {
            if (channel.label === "file") {
                this.onFileDataChannel(channel);
                return;
            }
            const registryData = this.dataChannelRegistry.get(channel.label);
            if (registryData === undefined) {
                throw new Error(`received unsupported channel "${channel.label}"`);
            }
            channel.binaryType = "arraybuffer";
            registryData.onSetChannel(channel);
            channel.onopen = () => {
                const deferred = channelPromises.pop();
                if (deferred !== undefined) {
                    deferred.resolve();
                }
            };
            if (channel.label === "reliable") {
                channel.onmessage = ({ data }) => {
                    if (typeof data === "string") {
                        this.onMessage(data, channel.label);
                        return;
                    }
                    this.handleMessage(data, channel.label);
                };
            }
            else {
                channel.onmessage = ({ data }) => {
                    this.handleMessage(data, channel.label);
                };
            }
            this.dataChannelRegistry.delete(channel.label);
        };
    }
    initDataChannels() {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            try {
                if (this.initiator) {
                    this.initInitiatorDataChannels(promises);
                }
                else {
                    this.initOtherDataChannel(promises);
                }
                yield Promise.all(promises).then(() => {
                    for (const cb of this.onChannelsOpenedCallbacks) {
                        cb();
                    }
                });
            }
            catch (error) {
                console.error("[WebRTC]", error);
            }
        });
    }
    resolveOnOpenChannel(channel) {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve) => {
                channel.onopen = () => {
                    resolve();
                };
            });
        });
    }
    onMessage(data, channelLabel) {
        const messageData = this.partialyReceivedMessage ? this.partialyReceivedMessage + data : data;
        if (messageData.endsWith("@")) {
            try {
                const json = JSON.parse(messageData.slice(0, -1));
                for (const cb of this.onMessageCallbacks) {
                    cb(json, channelLabel);
                }
                this.partialyReceivedMessage = undefined;
            }
            catch (e) {
                console.error(e, data);
            }
        }
        else {
            this.partialyReceivedMessage = messageData;
        }
    }
    setStats(peerStatistics, label, stats) {
        const s = peerStatistics.get(label);
        if (!s) {
            peerStatistics.set(label, stats);
        }
        else {
            Object.assign(s, stats);
        }
    }
    onFileDataChannel(channel) {
        channel.binaryType = "arraybuffer";
        const fileData = {
            channel,
            state: FileState.INIT,
            buffers: [],
            fileType: "file",
            type: ""
        };
        channel.onopen = () => {
            fileData.state = FileState.WAIT_FOR_METADATA;
        };
        channel.onerror = (error) => {
            if (fileData.fileId) {
                busConnection.send(new PeerFileErrorMessage(this.remoteClientId, fileData.fileId, JSON.stringify(error.error)));
            }
        };
        channel.onclose = () => {
            if (fileData.fileId) {
                busConnection.send(new PeerFileChannelClosedMessage(this.remoteClientId, fileData.fileId));
            }
        };
        channel.onmessage = ({ data }) => {
            if (data === END_OF_FILE_MESSAGE) {
                fileData.state = FileState.FINISHED;
                let received;
                if (fileData.fileType === "file") {
                    received = new Blob(fileData.buffers, { type: fileData.type });
                }
                else {
                    received = "";
                    for (const buf of fileData.buffers) {
                        received += buf;
                    }
                }
                if (fileData.fileId !== undefined) {
                    busConnection.send(new PeerFileMessage(this.remoteClientId, fileData.fileId, received, fileData.creator, fileData.owner));
                }
                channel.close();
                return;
            }
            else if (fileData.state === FileState.WAIT_FOR_METADATA) {
                const metaData = JSON.parse(data);
                const filename = metaData.filename;
                fileData.fileId = metaData.fileId;
                fileData.state = FileState.LOADING;
                fileData.fileType = metaData.fileType;
                fileData.creator = metaData.creator;
                fileData.owner = metaData.owner;
                fileData.type = metaData.type;
                busConnection.send(new PeerFileChannelOpenedMessage(this.remoteClientId, fileData.fileId, filename));
                if (metaData.fileTypeExtension === "bag")
                    busConnection.send(new LoadingSpinnerMessage(true));
                return;
            }
            fileData.buffers.push(data);
        };
        if (channel.id != null) {
            this.dataChannelFiles.set(channel.id, fileData);
        }
    }
    logUpdate(type, value = "") {
        const date = new Date();
        if (DEBUG_PRINT_LOGS)
            console.log(`logUpdate: ${type} `, value);
        if (typeof value === "object") {
            value = JSON.stringify(value);
        }
        this.updateLog.push({
            time: date.toLocaleString(),
            type,
            value
        });
    }
    setLocalDescription(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.logUpdate("setLocalDescription", JSON.stringify(desc));
            yield this.peerConnection.setLocalDescription(desc);
            this.logUpdate("setLocalDescriptionOnSuccess");
        });
    }
    setRemoteDescription(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection) {
                throw new Error("PeerConnection not set");
            }
            this.logUpdate("setRemoteDescription", desc);
            yield this.peerConnection.setRemoteDescription(desc);
            this.logUpdate("setRemoteDescriptionOnSuccess");
        });
    }
    createDataChannel(label, dataChannelDict) {
        if (!this.peerConnection) {
            throw new Error("PeerConnection not set");
        }
        this.logUpdate("createLocalDataChannel", Object.assign({ label }, dataChannelDict));
        return this.peerConnection.createDataChannel(label, dataChannelDict);
    }
    handleIceNegotiationFailed() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.iceCompleted) {
                console.warn("ICE connection failed after success", this.remoteClientId);
            }
            else {
                console.warn("ICE connection failed before success", this.remoteClientId);
            }
            if (this.iceNegotiationTimeout !== undefined) {
                clearTimeout(this.iceNegotiationTimeout);
                this.iceNegotiationTimeout = undefined;
            }
            if (this.restartICETimeout !== undefined) {
                clearTimeout(this.restartICETimeout);
                this.restartICETimeout = undefined;
            }
            yield this.restartIce();
        });
    }
    checkIsUsingTurn() {
        this.isUsingTurn = false;
        if (!this.peerConnection)
            return false;
        const receivers = this.peerConnection.getReceivers();
        if (!receivers)
            return false;
        for (const receiver of receivers) {
            if (!receiver.transport)
                continue;
            if (!receiver.transport.iceTransport)
                continue;
            const selectedPair = receiver.transport.iceTransport.getSelectedCandidatePair();
            if (!selectedPair)
                continue;
            const selectedLocalCandidate = selectedPair.local;
            const candidateType = selectedLocalCandidate.candidateType || selectedLocalCandidate.type;
            const isUsingTurn = candidateType === "relay";
            if (isUsingTurn) {
                this.isUsingTurn = isUsingTurn;
                return true;
            }
        }
        return false;
    }
    processPendingIceCandidates() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.peerConnection)
                return;
            if (this.pendingIceCandidates.length === 0)
                return;
            for (const c of this.pendingIceCandidates) {
                this.logUpdate("addIceCandidate", c);
                yield this.peerConnection.addIceCandidate(new RTCIceCandidate(c)).catch((error) => {
                    console.error("[WebRTC] Error in addIceCandidate", error);
                });
            }
            this.pendingIceCandidates = [];
        });
    }
    checkSignalingStateIs(info, state) {
        if (!this.peerConnection)
            return;
        if (this.peerConnection.signalingState === state)
            return;
        console.warn(`[WebRTC] ${info}: expected signaling state "${state}", but is ${this.peerConnection.signalingState}`);
    }
    setDisconnectTimeout() {
        if (this.disconnectedTimeout !== undefined) {
            return;
        }
        this.disconnectedTimeout = window.setTimeout(this.onDisconnectTimeout, DISCONNECT_TIMEOUT);
    }
    clearDisconnectTimeout() {
        if (this.disconnectedTimeout === undefined) {
            return;
        }
        clearTimeout(this.disconnectedTimeout);
        this.disconnectedTimeout = undefined;
    }
}
